import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector: 'sg2-treeview',
  templateUrl: './treeview.component.html',
  styleUrls: ['./treeview.component.less'],
  providers: []
})

export class TreeviewComponent {

  // @TODO scrolling bij het dragndrop reorderen
  // zie: https://stackblitz.com/edit/cdk-drop-list-scroll?file=app%2Fcdk-drag-drop-sorting-example.ts

  markingDisabled = false;
  query: string;
  showSelected: boolean = null;
  showExpandMore = false;
  dragging = false;
  treenodes: any[];
  previousSelectedElements = [];
  _nodes: any[];
  _selectedNodes: any[];

  // Quick hand for currently marked node
  marked: any;

  /**
   * Optionally: selectable, enables the selection of all nodes
   */
  @Input() selectable = false;

  /**
   * Optionally: orderable, enables sorting of elemetns
   */
  @Input() orderable = false;

  /**
   * Optionally: Set false to disable clickable parentnodes
   */
  @Input() parentClickable = true;

  /**
   * Start with all grandchildren expanded? defaults to tre
   */
  @Input() startExpanded = false;

  /**
   * Show chapter information if it is available
   */
  @Input() showChapter = false;

  /**
   * Should options menu per node to change it's name or add children etc.
   */
  @Input() showOptions = false;

  /**
   * Click handler
   */
  @Output() nodeClick = new EventEmitter<any>();

  @Output() menuClick = new EventEmitter<any>();

  /**
   * Event fires on any change to the selection (or selection order) always returns selection id array
   */
  @Output() selectChange = new EventEmitter<any>();

  /**
   * Event fires on any change to the order. Always returns sorted full element list
   */
  @Output() orderChange = new EventEmitter<any>();

  /**
   * Set to a ref id to mark (hightlight) a node in the tree
   * Can be used in an external scroll event
   */
  get mark(): string {
    return this.marked.ref;
  }

  @Input('mark')
  set mark(value: string) {
    if (!this.markingDisabled) {
      this.setMarkedByRef(value);
    }
  }

  /**
   * Nodes - required. Flat list of node elements
   */
  @Input()
  set nodes(nodes: any[]) {
    if (nodes && nodes.length) {
      this._nodes = nodes;
      this.buildTreeNodes();
    }
  }
  get nodes(): any[] {
    return this._nodes;
  }

  constructor(
    public authService: AuthService
  ) { }

  applyFilter() {
    this.findFiltered(this.treenodes);
  }

  clearFilter() {
    this.query = '';
    this.applyFilter();
  }

  findFiltered(nodes, isParentfound = false) {
    let foundAny = false;
    for (const node of nodes) {
      if (isParentfound || node.name.toLowerCase().includes(this.query.toLowerCase())) {
        node.filtered = false;
        foundAny = true;
        node.expanded = foundAny;
        if (node.children && node.children.length) {
          this.findFiltered(node.children, true);
        }
      } else if (node.children && node.children.length) {
        // No match? We should check deeper
        node.filtered = !this.findFiltered(node.children);

        foundAny = foundAny || !node.filtered;
        node.expanded = foundAny;
      } else {
        node.filtered = true;
      }
    }

    return foundAny;
  }

  /**
   * Opens/closes a node
   */
  toggleExpand(node) {
    node.expanded = !node.expanded;
    this.showExpandMore = !this.treenodes.some(n => n.expanded);
  }

  /**
   * Handle click of a node, and emitting the click event
   * @param node Node.
   */
  nodeClickHandler(node) {
    // unmark everything
    this.treenodes.map(n => {
      n.marked = false;
      if (n.children) {
        n.children.map(c => {
          c.marked = false;
          if (c.children) {
            c.children.map(gc => gc.marked = false);
          }
        });
      }
    });

    // mark current node
    node.marked = true;
    this.marked = node;

    // Disable mark functionality a short time after a click to prevent any possible scroll actions
    this.markingDisabled = true;
    setTimeout(() => this.markingDisabled = false, 300);

    // event event
    this.nodeClick.emit(node);
  }

  /**
   * Handles a click in the node menu and fires the event
   * @param node Node.
   */
  nodeMenuClickHandler(action, node) {
    // event event
    this.menuClick.emit({node: node, action: action});
  }

  /**
   * Sends an event when checkbox is toggled
   * @param node Node.
   */
  checkboxClickHandler(node) {
    if (node.children) {
      node.children.map(n => {
        n.selected = node.selected;
        if (n.children) {
          n.children.map(gc => gc.selected = node.selected);
        }
        return n;
      });
    }

    // Check if is's parents state should change from/to selected
    this.updateParentsSelectedState();

    this._selectedNodes = [];
    this._selectedNodes.push(node);

    this.checkSelectionChanges();
  }

  /**
   * Handles drop event
   * @param event Event.
   */
  drop(nodelist: any[], event: CdkDragDrop<string[]>) {

    // dropped on the same stop?
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(nodelist, event.previousIndex, event.currentIndex);

    // If the component has selections, fire the selectChange event
    this.checkSelectionChanges();
    const currentNode = nodelist[event.currentIndex];
    // The element order has changed, fire the event
    this.orderChange.emit({node: currentNode, nodes: this.getFlatNodes(currentNode.type)});
  }

  /**
   * Checks if there is actually a change in the selection then emits event
   */
  checkSelectionChanges() {
    if (this.selectable) {
      const currentSelectedElements = this.getSelectedElementIds();
      currentSelectedElements.splice(0, 0, this._selectedNodes[0]);
      if (currentSelectedElements.join() !== this.previousSelectedElements.join()) {
        this.selectChange.emit(currentSelectedElements);
        this.previousSelectedElements = currentSelectedElements;
      }
      this.updateParentsSelectedState();
    }
  }

  /**
   * Transforms the flat array nodes to a hierarchical treenode
   */
  buildTreeNodes() {
    // Check if we already maintain a list
    let previousTreenodes;
    if (this.treenodes && this.treenodes.length) {
      previousTreenodes = Object.assign([], this.treenodes);
    }

    // empty existing treenodes before rebuilding
    this.treenodes = [];
    const tmpTreenodes = [];

    for (const node of this._nodes) {

      // make ref key for bookmarks
      let ref = 'module-' + node['moduleId'];

      // If there was a previous selection get that
      let previousModuleNode;
      if (previousTreenodes && previousTreenodes.length) {
        previousModuleNode = previousTreenodes.find(n => n.ref === ref);
      }

      // ensure existence of parent node
      tmpTreenodes[node['moduleId']] = tmpTreenodes[node['moduleId']] || {
        name: node['moduleName'],
        chapter: node['moduleChapter'],
        _id: node['moduleId'],
        selected: false,
        type: 'module',
        expanded: previousModuleNode ? previousModuleNode.expanded : this.startExpanded,
        ref: ref,
        filtered: false
      };

      // extend the bookmark ref
      ref += '-submodule-' + node['submoduleId'];

      // If there was a previous selection get that
      let previousSubmoduleNode;
      if (previousModuleNode && previousModuleNode.children) {
        previousSubmoduleNode = previousModuleNode.children.find(n => n.ref === ref);
      }

      // ensure existence of grand parent node
      tmpTreenodes[node['moduleId']][node['submoduleId']] = tmpTreenodes[node['moduleId']][node['submoduleId']] || {
        name: node['submoduleName'],
        chapter: node['submoduleChapter'],
        _id: node['submoduleId'],
        moduleId: node['moduleId'],
        type: 'submodule',
        selected: false,
        expanded: previousSubmoduleNode ? previousSubmoduleNode.expanded : this.startExpanded,
        children: [],
        ref: ref,
        filtered: false
      };

      // create reference id
      ref = ref + '-element-' + node['_id'];

      // push child
      tmpTreenodes[node['moduleId']][node['submoduleId']].children.push({
        name: node['name'],
        label: node['label'],
        chapter: node['chapter'],
        selected: node['selected'],
        moduleId: node['moduleId'],
        type: 'element',
        submoduleId: node['submoduleId'],
        _id: node['_id'],
        ref: ref,
        filtered: false
      });

    }

    // convert to array
    for (const key in tmpTreenodes) {
      if (tmpTreenodes.hasOwnProperty(key)) {
        for (const subkey in tmpTreenodes[key]) {

          if (tmpTreenodes[key].hasOwnProperty(subkey)
            && !['name', 'chapter', 'expanded', '_id', 'ref', 'filtered', 'selected', 'moduleId', 'submoduleId', 'type'].includes(subkey)) {
            tmpTreenodes[key].children = tmpTreenodes[key].children || [];
            tmpTreenodes[key].children.push(tmpTreenodes[key][subkey]);
            delete tmpTreenodes[key][subkey];
          }
        }
        this.treenodes.push(tmpTreenodes[key]);
      }
    }

    // make sure all parent checkboxes are set
    this.updateParentsSelectedState();

    // If there is no marked item yet, automark first item
    if (!this.marked && this.treenodes[0]) {
      this.marked = this.treenodes[0];
      this.marked.marked = true;
    }

    // make sure possible items are marked
    if (this.marked) {
      this.setMarkedByRef(this.marked.ref);
    }
  }

  /**
   * Gets a flat id list of all selected elements, in correct order
   */
  getSelectedElementIds() {
    const elements = [];
    for (const node of this.treenodes) {
      for (const child of node.children) {
        for (const grandchild of child.children) {
          if (grandchild.selected) {
            elements.push(grandchild._id);
          }
        }
      }
    }
    return elements;
  }

  /**
   * Gets a flat list of all elements, in correct order
   */
  getFlatNodes(type) {
    const elements = [];
    const modules = [];
    const submodules = [];
    for (const node of this.treenodes) {
      modules.push(node);
      for (const child of node.children) {
        submodules.push(child);
        for (const grandchild of child.children) {
          elements.push(grandchild);
        }
      }
    }

    if (type === 'element') {
      return elements;
    } else if (type === 'submodule') {
      return submodules;
    }

    return modules;
  }

  /**
   * Handles basic filtering on selected state
   * @param state State.
   */
  setShowSelected(state) {
    this.showSelected = state;
  }

  /**
   * Opens all nodes
   */
  toggleExpandAll(value) {

    for (const node of this.treenodes) {
      node.expanded = value;
      for (const child of node.children) {
        child.expanded = value;
      }
    }

    this.showExpandMore = !value;
  }

  private updateParentsSelectedState() {
    this.treenodes.map((node) => {
      node.children.map((child) => {

        let selectedGCNumber: number | boolean = child.children.filter(gc => gc.selected === true).length ;

        // if none of the children of a child are selected,
        // then the child is false
        // elseif all of the children of a child are selected,
        // then the child is true
        // else the child will be halfselected,
        selectedGCNumber = selectedGCNumber || false;
        child.selected = selectedGCNumber === child.children.length ? true : selectedGCNumber;
      });

      let selectedGrandChildrenNumber: number | boolean = 0;
      let allGrandChildrenNumber: number | boolean = 0;

      node.children.map((child) => {
        selectedGrandChildrenNumber += child.children.filter(gc => gc.selected === true).length;
        allGrandChildrenNumber += child.children.length;
      });

      // if none of the grandchildren of a treednode are selected,
      // then the treenode is false
      // elsif all of the grandchildren of a treednode are selected,
      // then the treenode is true
      // else the treenode will be halfselected,
      selectedGrandChildrenNumber = selectedGrandChildrenNumber || false;
      node.selected = selectedGrandChildrenNumber === allGrandChildrenNumber ? true : selectedGrandChildrenNumber;
    });
  }

  dragStarted(event) {
    this.dragging = true;
  }

  dragEnded() {
    this.dragging = false;
  }


  setMarkedByRef(ref) {
    if (this.treenodes) {
      this.filterMarkedByRef(this.treenodes, ref);

      const el = document.getElementById('treeview-ref-' + this.marked.ref);
      if (el) {
        el.scrollIntoView(false);
      }
    }
  }

  filterMarkedByRef(root, ref) {
    root.filter(node => {
      node.marked = node.ref === ref;
      if (node.marked) {
        this.marked = node;
      }
      if (node.children) {
        this.filterMarkedByRef(node.children, ref);
      }
    });
  }
}
